import React, { useEffect, useMemo, useState } from 'react';
import PCConsoleTopCharts from '@intelligence/pages/TopCharts/PCConsoleGames/PCConsoleTopCharts';
import {
  TopchartPageId,
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { PCConsoleSegementList } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import {
  getPCConsoleMetricValues,
  getDeviceValuesByPageId,
} from '@intelligence/pages/TopCharts/PCGames/_config/utils';
import { defaultMetrictableApiParams, defaultMetacritic } from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils/constant';
import { getCascaderMetricsOptionsAndValue } from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import { buriedPointPage, typeIdObj } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes, dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { fetchGuideStatus } from '@/pages/intelligence/services/topcharts';
import { hasShortLinkRatio } from '@intelligence/_config/urlPageValidCfg';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

const PCConsoleMetricsTable: React.FC = () => {
  const entityType = PC_CONSOLE_COMBINE_ENTITY_TYPE;
  const businessType = 'pc-console-topCharts';
  const downloadKey = 'pcConsoleTopCharts';
  const pageId = TopchartPageId.PCConsoleMetricsTable;
  const hasUrlShortLink = hasShortLinkRatio.topPCConsoleMetricTable;
  const downloadLogConfigKey = `${entityType}_${pageId}_TopChartsMetricsTableDownload`;
  const downloadBtnLogConfigKey = `${entityType}_${pageId}_TopChartsMetricsTableDownloadBtn`;

  const {
    pcConsoleCascaderMetricsOptions,
    pcConsoleCascaderMetricsMap,
    pcConsoleDefaultMetrics,
  } = PCConsoleSegementList;

  const deviceValues = useMemo(() => getDeviceValuesByPageId(pageId, entityType), [pageId, entityType]);

  const tableColumnsListCur = useMemo(() => {
    const mappedList = pcConsoleDefaultMetrics
      .map((metricKey) => pcConsoleCascaderMetricsMap?.[metricKey])
      .filter((metric: any) => metric && metric?.value);
    if (mappedList.length) {
      return mappedList;
    }
    return pcConsoleDefaultMetrics.map((item) => ({
      value: item,
      dataSource: 'steamcharts',
      metricValue: 'steamcharts',
      text: 'dbi_metric_pcu',
    }));
  }, [pcConsoleDefaultMetrics, pcConsoleCascaderMetricsMap]);

  const defaultParams = useMemo(() => {
    const orderMetricParams = getPCConsoleMetricValues(tableColumnsListCur);
    return {
      ...defaultMetrictableApiParams,
      entity_type: entityType,
      order_metric: orderMetricParams?.order_metric,
      order_source: orderMetricParams?.order_source,
      source_metrics: {
        ...orderMetricParams?.source_metrics,
        ...(entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE ? defaultMetacritic : {}),
      },
      topchart_name: '',
      region_type: 'market',
      regions: ['global'],
      date_type: 'weekly',
      device: deviceValues,
    } as any;
  }, [tableColumnsListCur, entityType, deviceValues]);

  const { options: initialMetricSelectorOptionsTemp, value: initialMetricSelectorValues } =
    getCascaderMetricsOptionsAndValue(
      pageId,
      pcConsoleCascaderMetricsOptions,
      pcConsoleDefaultMetrics,
      defaultParams,
    );

  const initialMetricSelectorOptions = useMemo(
    () => initialMetricSelectorOptionsTemp,
    [initialMetricSelectorOptionsTemp],
  );

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.combileMetricTable?.type,
    // 仅本页把默认时间查询从 cond 切到 getDefaultDataByDate（走 model 内的 fetchTopChartsMaxDate）
    isUseCond: false,
  });

  const [firstDownLoadBubleShow, setFirstDownLoadBubleShow] = useState(false);

  useEffect(() => {
    const getGuideStatus = async () => {
      try {
        const res = await fetchGuideStatus({
          button_id: dbiBtnLogMes.topchartMobileGameActiveUserDownLoadTip.buttonId,
          type_id: typeIdObj.intelligence,
        });
        if (res?.code === 0) {
          setFirstDownLoadBubleShow(res?.data);
        }
      } catch (error) {
        // ignore errors fetching guide status; defaults keep bubble hidden
      }
    };

    buriedPointPage(dbiPageLogMes.topChartsPCConsoleGamesMetricsTable);
    getGuideStatus();
  }, []);

  return (
    <>
      <MarqueeBanner />
      <PCConsoleTopCharts
        entityType={entityType}
      pageId={pageId}
      businessType={businessType}
      downloadKey={downloadKey}
      downloadLogConfigKey={downloadLogConfigKey}
      downloadBtnLogConfigKey={downloadBtnLogConfigKey}
      hasUrlShortLink={hasUrlShortLink}
      initialMetricSelectorValues={initialMetricSelectorValues}
      initialMetricSelectorOptions={initialMetricSelectorOptions}
      pcConsoleCascaderMetricsMap={pcConsoleCascaderMetricsMap}
      pcConsoleCascaderMetricsOptions={pcConsoleCascaderMetricsOptions}
      pcConsoleDefaultMetrics={pcConsoleDefaultMetrics}
      tableColumnsListCur={tableColumnsListCur}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      defaultParams={defaultParams}
      firstDownLoadBubleShow={firstDownLoadBubleShow}
      setFirstDownLoadBubleShow={setFirstDownLoadBubleShow}
      pageLogKey="topChartsPCConsoleGamesMetricsTable"
      pageLogInfo={dbiPageLogMes.topChartsPCConsoleGamesMetricsTable}
    />
    </>
  );
};

export default PCConsoleMetricsTable;
